import { useEffect, useState } from 'react'

import type { FilterFieldDef, FilterRenderCtx, FilterSurface } from './types'
import { Input } from '@/components/ui/input'
import { useDebounced } from '@/hooks/use-debounced'
import { formatVND } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

interface AmountFilterOptions {
    label: () => string
    /** Chờ bao lâu sau khi ngừng gõ mới đẩy lên URL. */
    debounceMs?: number
}

const controlClass = (surface: FilterSurface) =>
    surface === 'sheet' ? 'h-11 w-full' : 'h-9 w-full'

const digitsOnly = (raw: string) => raw.replace(/\D/g, '')
const grouped = (digits: string) =>
    digits === '' ? '' : formatVND(Number(digits))

/**
 * Ô nhập số tiền: giữ bản nháp cục bộ, hiện dấu chấm hàng nghìn, debounce rồi
 * mới báo ra ngoài chuỗi số thô. Cùng khuôn với ô tìm kiếm (effect đẩy ra và
 * effect đồng bộ ngược khi URL đổi từ nơi khác).
 */
function AmountFilterControl({
    id,
    value,
    onChange,
    surface,
    debounceMs,
}: FilterRenderCtx<string> & { debounceMs: number }) {
    const [draft, setDraft] = useState(value)
    const debounced = useDebounced(draft, debounceMs)

    // Cố ý chỉ phụ thuộc `debounced` — xem textFilter.
    useEffect(() => {
        if (debounced !== value) onChange(debounced)
    }, [debounced])

    useEffect(() => {
        setDraft(value)
    }, [value])

    return (
        <Input
            id={id}
            inputMode="numeric"
            placeholder={m.list_filter_amount_placeholder()}
            className={controlClass(surface)}
            value={grouped(draft)}
            onChange={(e) => setDraft(digitsOnly(e.target.value))}
        />
    )
}

/**
 * Lọc theo một mốc số tiền VND (nguyên, không âm), kiểu ô "Từ … / Tới …" của
 * KiotViet. Giá trị là chuỗi số thô, `''` = không lọc; URL ghi number.
 */
export function amountFilter({
    label,
    debounceMs = 400,
}: AmountFilterOptions): FilterFieldDef<string> {
    return {
        defaultValue: '',
        label,
        parse: (raw) => {
            const text = typeof raw === 'number' ? String(raw) : raw
            return typeof text === 'string' && /^\d+$/.test(text)
                ? String(Number(text))
                : undefined
        },
        serialize: (value) => (value === '' ? undefined : Number(value)),
        chipLabel: (value) => `${label()}: ${grouped(value)}`,
        render: (ctx) => (
            <AmountFilterControl {...ctx} debounceMs={debounceMs} />
        ),
    }
}
